import { CompanyContext } from "contexts/CompanyContext";
import { SessionContext } from "contexts/SessionContext";
import { useContext } from "react";
import { api as Api } from "api/http";
import { formatSocialPostObj } from "../../formatSocialPostObj";
import { Notification, toast } from "components/ui";
import { getSocialNetworkIntegration } from "utils/getSocialNetworkIntegration";

const resolveAttachmentType = (att) => {
    const mediaType = att?.media?.type;
    if (mediaType === "video") return "video";
    if (mediaType === "photo") return "image";
    const t = att?.type || "";
    if (t.includes("video")) return "video";
    if (t === "photo" || t === "profile_media" || t === "cover_photo" || t === "album") return "image";
    return mediaType || t || "image";
};

const resolveAttachmentUrls = (att, type) => {
    const stillImage = att?.media?.image?.src;
    const source = att?.media?.source;
    if (type === "video") {
        return { url: source || stillImage, thumbnail_url: stillImage || source };
    }
    return { url: stillImage || source, thumbnail_url: stillImage || source };
};

export const useFacebook = () => {

    const { companySocials, company } = useContext(CompanyContext);
    const _start_date = new Date(new Date().getTime() - 90 * 24 * 3600 * 1000);
    const _end_date = new Date();
    const network = "facebook";

    const clearExpiredCache = (socialNetwork) => {
        const currentTime = Date.now();
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(`${socialNetwork.id}_posts_`)) {
                const cachedData = JSON.parse(localStorage.getItem(key));
                if (currentTime > cachedData.timestamp + 1000 * 60 * 2) {
                    localStorage.removeItem(key);
                }
            }
        });
    };

    const sendFacebookMessage = async (conversation, message) => {

        const socialNetwork = getSocialNetworkIntegration(network, companySocials);
        if (!socialNetwork) return [];

        const _message = await Api.post(`/messages`, {}, {
            conversation_id: conversation.id,
            message: message
        }).then(({ data: message }) => {
            return message;
        }).catch((err) => {
            toast.push(<Notification type="danger" title={err?.message || err} />);
        })

        return _message;
    }

    const deleteFacebookMessage = async (message_id) => {
        
        const socialNetwork = getSocialNetworkIntegration(network, companySocials);
        if (!socialNetwork) return [];

        try {
            const response = await Api.delete(`/messages/${message_id}`);
        } catch (e) {
            let message = e?.message;
            if (message) {
                message = message.split(`"error":{"message":"`);
                if (message?.length > 1) {
                    message = message[1];
                    message = message.split(`","type"`)?.[0];
                } else {
                    message = null;
                }
            }
            toast.push(<Notification type="danger" title={message || e?.message || `An error ocurred while deleteing the message`} />);
            return false;
        }
        
        return true;
    }

    const getFacebookConversationMessages = async (
        conversation_id,
        per_page = 100, 
    ) => {

        const socialNetwork = getSocialNetworkIntegration(network, companySocials);
        if (!socialNetwork) return [];

        const messages = await Api.get(`/messages?filter[conversation_id]=${conversation_id}&sort=-id&include=binaries`).then(
            ({ data: messages }) => messages);
        
        return messages;
    }

    const getFacebookConversations = async (filters) => {

        const socialNetwork = getSocialNetworkIntegration(network, companySocials);
        if (!socialNetwork) return [];

        let filterArray = [];
        filterArray.push(`filter[social_network_id]=${socialNetwork.id}`);
        filterArray.push(`filter[externalUser.company_id]=${company.id}`);
        if (!!filters?.query) {
            filterArray.push(`filter[externalUser.name]=${filters?.query}`);
        }
        if (!!filters?.assignedTo) {
            filterArray.push(`filter[user_id]=${filters?.assignedTo}`);
        }
        if (!!filters?.unread) {
            filterArray.push(`filter[has_unreadMessages]=${1}`);
        }

        try {
            const conversations = await Api.get(`/conversations?${filterArray.join("&")}&sort=lastMessage&include=lastMessage,unreadMessagesCount,externalUser.image`).then(({ data: conversations }) => {
                conversations = conversations.map(c => ({...c, network: network}));
                return conversations;
            });
            return conversations;
        } catch (err) {}
        
        return [];
    }

    const getFacebookPostsInsights = async (
        per_page = 100, // 100
        start_date = _start_date,
        end_date = _end_date
    ) => {
        const socialNetwork = getSocialNetworkIntegration("facebook", companySocials);
        if (!socialNetwork) return [];

        return getFacebookPosts(per_page, start_date, end_date);
    }


    const getFacebookPosts = async (
        per_page = 100,
        start_date = _start_date,
        end_date = _end_date
    ) => {
        const socialNetwork = getSocialNetworkIntegration("facebook", companySocials);
        if (!socialNetwork) return [];

        const formatDate = (date) => {
            return new Date(date).toISOString().split('T')[0];
        };

        if (start_date) start_date = new Date(start_date).toISOString();
        if (end_date) end_date = new Date(end_date).toISOString();

        const cacheKey = `${socialNetwork.id}_posts_${start_date ? formatDate(start_date) : 'all'}_${end_date ? formatDate(end_date) : 'all'}`;

        // Clear expired cache entries
        clearExpiredCache(socialNetwork);

        // Check cache
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
            const { posts, timestamp } = JSON.parse(cachedData);
            if (Date.now() <= timestamp + 1000 * 60 * 2) {
                return posts;
            }
        }

        // Fetch posts and insights in parallel. Insights metric names occasionally
        // get deprecated by Facebook (e.g. post_impressions returning #100 errors); a
        // failure on the insights call must not block the post grid from rendering.
        const dateAndLimitQuery = `${start_date ? `&since=${Math.ceil(new Date(start_date).getTime() / 1000)}` : ``}${end_date ? `&until=${Math.ceil(new Date(end_date).getTime() / 1000)}` : ``}${per_page ? `&limit=${per_page}` : ``}`;
        const baseFields = `attachments,created_time,from,full_picture,message,permalink_url`;
        const insightsFields = `id,insights.metric(post_impressions,post_activity_by_action_type)`;

        const [postsResult, insightsResult] = await Promise.allSettled([
            Api.get(`/socialNetworks/${socialNetwork.id}/posts?${dateAndLimitQuery}&fields=${baseFields}`),
            Api.get(`/socialNetworks/${socialNetwork.id}/posts?${dateAndLimitQuery}&fields=${insightsFields}`),
        ]);

        if (postsResult.status === 'rejected') throw postsResult.reason;

        const insightsByPostId = new Map();
        if (insightsResult.status === 'fulfilled') {
            (insightsResult.value?.data || []).forEach((p) => {
                if (p?.id) insightsByPostId.set(p.id, p.insights);
            });
        } else {
            console.error('Error fetching Facebook post insights (continuing without):', insightsResult.reason);
        }

        const fetchedPosts = (postsResult.value?.data || []).map((post) => {
                post.insights = insightsByPostId.get(post?.id) || { data: [] };
                return post;
            }).map((post) => {
                const firstAttachment = post?.attachments?.data?.[0];
                const subattachments = firstAttachment?.subattachments?.data;
                const fullPictureFallback = post?.full_picture;

                const buildMediaItem = (att) => {
                    const type = resolveAttachmentType(att);
                    const { url, thumbnail_url } = resolveAttachmentUrls(att, type);
                    return {
                        type,
                        url,
                        thumbnail_url: thumbnail_url || fullPictureFallback,
                        width: att?.media?.image?.width,
                        height: att?.media?.image?.height
                    };
                };

                let media = subattachments?.length > 0
                    ? subattachments.map(buildMediaItem)
                    : firstAttachment
                        ? [buildMediaItem(firstAttachment)]
                        : [];

                media = media?.filter(e => !!e.url);

                if (!media?.[0]?.type && !post?.message) return undefined;

                return formatSocialPostObj(
                    "facebook",
                    post?.id,
                    post?.from?.id,
                    post?.from?.name,
                    "",
                    "",
                    post?.created_time,
                    post?.message,
                    media,
                    post?.insights?.data?.filter(e => e?.name == "post_activity_by_action_type")?.[0]?.values?.[0]?.value?.like || 0,
                    post?.insights?.data?.filter(e => e?.name == "post_activity_by_action_type")?.[0]?.values?.[0]?.value?.comment || 0,
                    post?.insights?.data?.filter(e => e?.name == "post_impressions")?.[0]?.values?.[0]?.value || 0,
                    post?.insights?.data?.filter(e => e?.name == "post_activity_by_action_type")?.[0]?.values?.[0]?.value?.share || 0,
                    undefined,
                    undefined,
                    undefined,
                    "FEED",
                    undefined,
                    [],
                    media,
                    undefined,
                    0,
                    0,
                    post?.comments_to_create,
                    post?.cover
                )
            }).filter(e => e != undefined);

        // Update cache
        localStorage.setItem(cacheKey, JSON.stringify({ posts: fetchedPosts, timestamp: Date.now() }));

        return fetchedPosts;
    }

    const getFacebookComments = async (
        id
    ) => {

        const socialNetwork = getSocialNetworkIntegration("facebook", companySocials);
        if (!socialNetwork) return [];

        const comments = await Api.get(`/comments?sort=-id&filter[parent_id]=null&filter[external_post_id]=${id}&include=externalUser.image,children.externalUser.image`).then(({ data }) => data);

        return comments;
    }

    const replyToFacebookComment = async (
        comment,
        message,
    ) => {

        const socialNetwork = getSocialNetworkIntegration("facebook", companySocials);
        if (!socialNetwork) return [];

        await Api.post(`/comments`, {}, {
            external_comment_id: comment?.external_id,
            message: message
        });
    }
    
    return { 
        getFacebookPosts, 
        getFacebookPostsInsights, 
        getFacebookConversations, 
        getFacebookConversationMessages, 
        sendFacebookMessage, 
        getFacebookComments, 
        replyToFacebookComment,
        deleteFacebookMessage 
    }
}


